// GA4 event helpers for the carrier signup funnel.
//
// gtag is loaded in public/index.html, and only in production builds — outside prod
// `window.gtag` is deliberately absent, so every call here is a no-op rather than a fault.
// Same guard shape as metaConversion.ts.
//
// These events replace the prequal-era ones the landing page still emits. They are named for
// stages in the admin portal rather than for screens, so a number in GA4 can be reconciled
// against the same number in the portal — which is the whole point of the exercise.

import { ApplicationStep } from '../types/apiTypes';
import { safeGetItem, safeSetItem } from '../shared/safeStorage';

declare global {
  interface Window {
    gtag?: (
      command: string,
      eventName: string,
      params?: Record<string, any>
    ) => void;
  }
}

export enum Ga4Event {
  /** A carrier created a login and reached screen one. Observation only — not a bid target. */
  ApplicationStarted = 'application_started',
  /** A carrier cleared an application step. `step_label` says which one. */
  ApplicationStepCompleted = 'application_step_completed',
  /** A carrier chose where cards are mailed on Review (THT-819). `source` says which option. */
  MailingAddressChosen = 'mailing_address_chosen',
}

/**
 * Funnel-spine labels ('00'…'07') for the `step_label` parameter.
 *
 * Only the step whose completion we emit needs an entry. The later steps are deliberately not
 * emitted to GA4: they happen a median of 47 days after the click, frequently on another
 * device, so GA4 would systematically undercount them and disagree with the admin portal —
 * which already answers "where do people stall" from real timestamps. Add an entry here when a
 * transition genuinely needs to leave the building.
 */
export const FUNNEL_STEP_LABELS: Partial<Record<ApplicationStep, string>> = {
  [ApplicationStep.BusinessInfo]: '00',
};

/**
 * Where the last reported step completion is remembered.
 *
 * One key holding one application id, not an accumulating set: a carrier has a single
 * application in flight, so the last id answered is all that has to be remembered and the key
 * cannot grow.
 */
const STEP_COMPLETION_REPORTED_KEY = 'tach_step_completion_reported';

/**
 * True the first time it is called for an application, false every time after. The caller that
 * gets `true` is the one that should emit; the claim is recorded as a side effect so a caller
 * cannot forget to.
 *
 * This has to outlive the component. BusinessInfo unmounts the moment the step advances (the
 * switch in pages/signup.tsx renders one step at a time), and four paths bring a carrier back to
 * screen one to submit again — Back from screen two, Edit from Review, the bad MC/DOT bounce,
 * and the carrier-identity edit. A component-local ref resets on every one of them, which is the
 * only situation it would ever have been asked about.
 *
 * Meta needs no help here — its event id is derived from the application id with no timestamp,
 * so a repeat pairs with the first and Meta records one conversion. GA4 has no equivalent, and
 * an inflated step count is precisely the disagreement with the admin portal these events exist
 * to remove.
 *
 * localStorage rather than sessionStorage so it also covers the carrier who comes back a day
 * later, navigates back to screen one and submits again.
 *
 * Reads and writes are guarded because blocked site data (Safari's "Block All Cookies", some
 * privacy modes) makes storage access THROW rather than return null — the same crash this app's
 * sign-in hit. When storage is unavailable this returns true and the event fires, which is the
 * un-guarded behaviour we have today rather than something worse.
 */
export const claimStepCompletionReport = (applicationId: string): boolean => {
  try {
    if (safeGetItem(STEP_COMPLETION_REPORTED_KEY) === applicationId) {
      return false;
    }
    safeSetItem(STEP_COMPLETION_REPORTED_KEY, applicationId);
  } catch {
    // Storage blocked — fall through and report. Better a possible double-count than a throw in
    // the middle of the signup flow.
  }
  return true;
};

/**
 * Fires a GA4 event. No-ops when gtag is absent (every non-prod build).
 */
export const trackGa4Event = (
  eventName: Ga4Event,
  params: Record<string, any> = {}
) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', eventName, params);
};

/**
 * Fires when a carrier creates a login and lands on screen one.
 *
 * No application exists at this point — the application row is not written until screen one is
 * submitted — so there is no id to attach. This is the count Brittany's portal reports as
 * "applications started".
 */
export const trackApplicationStarted = () =>
  trackGa4Event(Ga4Event.ApplicationStarted);

/**
 * Fires when a carrier clears an application step.
 *
 * `completedStep` is the step they just finished, not the one they are moving to. A step with
 * no entry in FUNNEL_STEP_LABELS is not emitted rather than emitted with an undefined label,
 * so a new step added to the enum cannot quietly produce malformed events.
 */
export const trackApplicationStepCompleted = ({
  completedStep,
  applicationId,
}: {
  completedStep: ApplicationStep;
  applicationId: string;
}) => {
  const stepLabel = FUNNEL_STEP_LABELS[completedStep];
  if (!stepLabel) return;

  trackGa4Event(Ga4Event.ApplicationStepCompleted, {
    step_label: stepLabel,
    step_name: completedStep,
    application_id: applicationId,
  });
};

/** Which kind of option was picked. `saved` is any saved address that is neither of the first two. */
export type MailingAddressSource = 'business' | 'home' | 'saved' | 'other';

/**
 * Fires when the carrier's mailing-address choice on Review has been applied. `source` is the
 * option picked (business | home | saved | other) — never the address itself.
 */
export const trackMailingAddressChosen = ({
  source,
}: {
  source: MailingAddressSource;
}) => trackGa4Event(Ga4Event.MailingAddressChosen, { source });
